import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  CircularProgress,
  Alert
} from '@mui/material';
import { Send as SendIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase-config';
import { getAuth } from 'firebase/auth';
import { isBusinessRelatedQuestion, findBestMatchingAnswer, getFallbackMessage } from '../utils/aiMatcher';

const TestAssistant = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [businessData, setBusinessData] = useState(null);
  const [userMessage, setUserMessage] = useState('');
  const [conversation, setConversation] = useState([
    { type: 'assistant', text: 'Welcome to the test interface. You can simulate a customer sending a message. Try asking a question to test how the AI responds.' }
  ]);

  const auth = getAuth();
  const userId = auth.currentUser?.uid;

  // Load business data
  useEffect(() => {
    const loadBusinessData = async () => {
      if (!userId) return;

      try {
        setLoading(true);
        const businessRef = doc(db, 'businesses', userId);
        const businessDoc = await getDoc(businessRef);

        if (businessDoc.exists()) {
          setBusinessData(businessDoc.data());

          // Add intro message to conversation if it exists
          if (businessDoc.data().introMessage) {
            setConversation(prev => [
              ...prev,
              { type: 'assistant', text: businessDoc.data().introMessage }
            ]);
          }
        } else {
          setError('Business profile not found');
        }
      } catch (err) {
        console.error('Error loading business data:', err);
        setError('Failed to load business data');
      } finally {
        setLoading(false);
      }
    };

    loadBusinessData();
  }, [userId]);

  const handleSendMessage = async () => {
    if (!userMessage.trim()) return;

    // Add user message to conversation
    setConversation(prev => [
      ...prev,
      { type: 'user', text: userMessage }
    ]);

    // Clear input field
    const message = userMessage;
    setUserMessage('');
    setLoading(true);

    try {
      // First check common questions for an exact match
      const matchedQA = businessData?.commonQuestions?.find(
        qa => qa.question.toLowerCase() === message.toLowerCase()
      );

      if (matchedQA) {
        // If there's an exact match, use the pre-defined answer
        setTimeout(() => {
          setConversation(prev => [
            ...prev,
            { type: 'assistant', text: matchedQA.answer }
          ]);
          setLoading(false);
        }, 1000);
        return;
      }

      // Otherwise, simulate AI response by sending to API
      // In a real implementation, this would call your backend API
      const response = await simulateAIResponse(message);

      setConversation(prev => [
        ...prev,
        { type: 'assistant', text: response }
      ]);
    } catch (err) {
      console.error('Error getting AI response:', err);
      setConversation(prev => [
        ...prev,
        { type: 'assistant', text: "I'm sorry, I encountered an error processing your request." }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const simulateAIResponse = async (message) => {
    // In a real implementation, this would call your backend API
    // For now, we'll just simulate a delay and return a generic response
    await new Promise(resolve => setTimeout(resolve, 1500));

    console.log('Testing AI response for message:', message);

    // Try to find relevant Q&A using the improved AI matching algorithm
    const commonQuestions = businessData?.commonQuestions || [];

    // First check if the question is business-related
    const isBusinessRelated = isBusinessRelatedQuestion(message);
    console.log(`Is business-related: ${isBusinessRelated}`);

    if (!isBusinessRelated) {
      console.log('Question is not business-related, using fallback message');
      return getFallbackMessage();
    }

    // Find the best matching answer using the improved algorithm
    const bestMatch = findBestMatchingAnswer(message, commonQuestions);

    // Use the best match if found, otherwise use the fallback message
    if (bestMatch) {
      console.log('Found matching answer:', bestMatch);
      return bestMatch;
    }

    // Return fallback message if no good match found
    console.log('No good match found, using fallback message');
    return getFallbackMessage();
  };

  const handleNavigateToQA = () => {
    navigate('/manage-qa');
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/dashboard')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5">Test Your AI Assistant</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="body1" paragraph>
          This is a simulation of how your AI assistant will respond to customer messages.
          Try asking questions to see how it responds based on your setup.
        </Typography>

        <Button variant="outlined" onClick={handleNavigateToQA} sx={{ mb: 2 }}>
          Manage Q&A
        </Button>
      </Paper>

      <Paper sx={{ p: 2, mb: 3, maxHeight: 400, overflow: 'auto' }}>
        <List>
          {conversation.map((message, index) => (
            <React.Fragment key={index}>
              <ListItem alignItems="flex-start" sx={{
                justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start',
                mb: 1
              }}>
                <Paper
                  elevation={1}
                  sx={{
                    p: 2,
                    maxWidth: '80%',
                    bgcolor: message.type === 'user' ? 'primary.light' : 'grey.100'
                  }}
                >
                  <Typography variant="body1">
                    {message.text}
                  </Typography>
                </Paper>
              </ListItem>
              {index < conversation.length - 1 && <Divider component="li" />}
            </React.Fragment>
          ))}
        </List>
      </Paper>

      <Paper sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Type a customer message..."
          value={userMessage}
          onChange={(e) => setUserMessage(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
          disabled={loading}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleSendMessage}
          disabled={!userMessage.trim() || loading}
          sx={{ ml: 2 }}
        >
          {loading ? <CircularProgress size={24} /> : <SendIcon />}
        </Button>
      </Paper>
    </Box>
  );
};

export default TestAssistant;